import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { getCompras, getCuentas, addPagoToCompra } from '../services/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { CreditCard, ArrowLeft, Check } from 'lucide-react';

const MEDIOS_PAGO = [
  { value: 'EFECTIVO', label: 'Efectivo' },
  { value: 'TRANSFERENCIA', label: 'Transferencia' },
  { value: 'TARJETA', label: 'Tarjeta' },
  { value: 'YAPE', label: 'Yape' },
  { value: 'PLIN', label: 'Plin' },
  { value: 'DEPOSITO', label: 'Depósito' },
];

const PagarFacturasPage = () => {
  const navigate = useNavigate();
  const { empresaActual } = useApp();
  const [facturas, setFacturas] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Selección
  const [selectedIds, setSelectedIds] = useState(new Set());
  
  // Datos de pago
  const [pagoConfig, setPagoConfig] = useState({
    cuenta_id: '',
    medio_pago: 'TRANSFERENCIA',
    ref_pago: ''
  });

  const loadData = useCallback(async () => {
    if (!empresaActual?.id) return;
    
    setLoading(true);
    try {
      const [facturasRes, cuentasRes] = await Promise.all([
        getCompras(empresaActual.id, 'FACTURA_PROVEEDOR', 'PENDIENTE'),
        getCuentas(empresaActual.id)
      ]);
      
      // Solo facturas con saldo pendiente y NO canjeadas por letras
      const facturasConSaldo = facturasRes.data.filter(f => {
        const saldo = f.total_me - (f.total_pagado_me || 0);
        return saldo > 0.01 && f.cxp_estado !== 'CANJEADO';
      }).map(f => ({
        ...f,
        saldo_pendiente: f.total_me - (f.total_pagado_me || 0),
        monto_pagar: (f.total_me - (f.total_pagado_me || 0)).toFixed(2)
      }));
      
      setFacturas(facturasConSaldo);
      setCuentas(cuentasRes.data.filter(c => c.activo));
      
      if (cuentasRes.data.length > 0) {
        setPagoConfig(prev => ({
          ...prev,
          cuenta_id: cuentasRes.data.filter(c => c.activo)[0]?.id?.toString() || ''
        }));
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [empresaActual?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Toggle selección
  const toggleSelect = (id) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  // Seleccionar/deseleccionar todos
  const toggleSelectAll = () => {
    if (selectedIds.size === facturas.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(facturas.map(f => f.id)));
    }
  };

  // Actualizar monto a pagar de una factura
  const updateMontoPagar = (id, monto) => {
    setFacturas(facturas.map(f => 
      f.id === id ? { ...f, monto_pagar: monto } : f
    ));
  };

  // Calcular total seleccionado
  const facturasSeleccionadas = facturas.filter(f => selectedIds.has(f.id));
  const totalAPagar = facturasSeleccionadas.reduce((sum, f) => sum + parseFloat(f.monto_pagar || 0), 0);

  // Pagar facturas seleccionadas
  const handlePagarTodas = async () => {
    if (selectedIds.size === 0) {
      toast.error('Seleccione al menos una factura');
      return;
    }
    
    if (!pagoConfig.cuenta_id) {
      toast.error('Seleccione una cuenta');
      return;
    }

    const facturasAPagar = facturasSeleccionadas.filter(f => parseFloat(f.monto_pagar) > 0);
    if (facturasAPagar.length === 0) {
      toast.error('Los montos a pagar deben ser mayores a 0');
      return;
    }

    setSaving(true);
    let pagosExitosos = 0;
    let pagosError = 0;

    for (const factura of facturasAPagar) {
      try {
        await addPagoToCompra(factura.id, {
          cuenta_id: parseInt(pagoConfig.cuenta_id),
          medio_pago: pagoConfig.medio_pago,
          monto: parseFloat(factura.monto_pagar),
          ref_pago: pagoConfig.ref_pago || `Pago batch - ${factura.nro_doc_full}`
        });
        pagosExitosos++;
      } catch (error) {
        pagosError++;
        console.error(`Error pagando factura ${factura.nro_doc_full}:`, error);
      }
    }

    setSaving(false);
    
    if (pagosExitosos > 0) {
      toast.success(`${pagosExitosos} pago(s) registrado(s)`);
    }
    if (pagosError > 0) {
      toast.error(`${pagosError} pago(s) con error`);
    }
    
    // Recargar datos
    setSelectedIds(new Set());
    loadData();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/compras/factura')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <CreditCard className="w-7 h-7 text-emerald-600" />
              Pagar Facturas
            </h1>
            <p className="text-gray-500">Pago por lotes de facturas pendientes</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Lista de facturas */}
        <div className="col-span-2">
          <Card>
            <CardContent className="p-0">
              <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <Checkbox 
                    checked={selectedIds.size === facturas.length && facturas.length > 0}
                    onCheckedChange={toggleSelectAll}
                  />
                  <span className="font-medium">
                    {selectedIds.size > 0 ? `${selectedIds.size} seleccionada(s)` : 'Seleccionar todas'}
                  </span>
                </div>
                <span className="text-sm text-gray-500">{facturas.length} factura(s) pendiente(s)</span>
              </div>
              
              {loading ? (
                <div className="p-8 text-center text-gray-500">Cargando...</div>
              ) : facturas.length === 0 ? (
                <div className="p-8 text-center text-gray-500">No hay facturas pendientes de pago</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12"></TableHead>
                      <TableHead>Documento</TableHead>
                      <TableHead>Proveedor</TableHead>
                      <TableHead>Vencimiento</TableHead>
                      <TableHead className="text-right">Saldo</TableHead>
                      <TableHead className="text-right w-32">A Pagar</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {facturas.map(factura => (
                      <TableRow key={factura.id} className={selectedIds.has(factura.id) ? 'bg-emerald-50' : ''}>
                        <TableCell>
                          <Checkbox 
                            checked={selectedIds.has(factura.id)}
                            onCheckedChange={() => toggleSelect(factura.id)}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{factura.nro_doc_full || '-'}</TableCell>
                        <TableCell>{factura.proveedor_nombre || '-'}</TableCell>
                        <TableCell className="text-orange-600">{factura.fecha_venc || '-'}</TableCell>
                        <TableCell className="text-right font-medium text-orange-600">
                          {factura.moneda_codigo} {factura.saldo_pendiente?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step="0.01"
                            value={factura.monto_pagar}
                            onChange={(e) => updateMontoPagar(factura.id, e.target.value)}
                            className="h-8 text-right"
                            disabled={!selectedIds.has(factura.id)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Panel de pago */}
        <div className="col-span-1">
          <Card className="sticky top-4">
            <CardContent className="p-6 space-y-6">
              <h3 className="font-semibold text-lg">Datos del Pago</h3>
              
              <div className="space-y-4">
                <div>
                  <Label>Cuenta *</Label>
                  <Select value={pagoConfig.cuenta_id} onValueChange={(v) => setPagoConfig({...pagoConfig, cuenta_id: v})}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Seleccione cuenta" />
                    </SelectTrigger>
                    <SelectContent>
                      {cuentas.map(c => (
                        <SelectItem key={c.id} value={c.id.toString()}>
                          {c.nombre} ({c.moneda_codigo})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Medio de Pago</Label>
                  <Select value={pagoConfig.medio_pago} onValueChange={(v) => setPagoConfig({...pagoConfig, medio_pago: v})}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MEDIOS_PAGO.map(m => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Referencia</Label>
                  <Input
                    className="mt-1"
                    value={pagoConfig.ref_pago}
                    onChange={(e) => setPagoConfig({...pagoConfig, ref_pago: e.target.value})}
                    placeholder="Nro. operación (opcional)"
                  />
                </div>
              </div>

              {/* Resumen */}
              <div className="border-t pt-4 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Facturas seleccionadas</span>
                  <span className="font-medium">{selectedIds.size}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Total a Pagar</span>
                  <span className="font-bold text-xl text-emerald-600">
                    S/. {totalAPagar.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>

              <Button 
                onClick={handlePagarTodas}
                disabled={saving || selectedIds.size === 0 || !pagoConfig.cuenta_id}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                size="lg"
              >
                {saving ? 'Procesando...' : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Pagar {selectedIds.size} Factura(s)
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PagarFacturasPage;
